import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { amountTone, formatMoney } from '../../utils/format';

interface SchoolYearSummary {
    schoolYear: string;
    totalIncome: number;
    totalSpending: number;
    totalRefunds: number;
    balance: number;
    accountBalance: number;
}

const SchoolYearSummaryDisplay: React.FC<{ refresh: boolean }> = ({ refresh }) => {
    const [summaries, setSummaries] = useState<SchoolYearSummary[]>([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        axios.get<SchoolYearSummary[]>('/api/data/school-year-summaries')
            .then(response => {
                setSummaries(response.data);
            })
            .catch(error => {
                console.error("Error fetching school year summaries:", error);
            })
            .finally(() => setLoaded(true));
    }, [refresh]);

    if (!loaded) {
        return <div className="loading-state"><span className="spinner-border spinner-border-sm" role="status" />Ładowanie…</div>;
    }

    if (summaries.length === 0) {
        return <div className="surface empty-state">Brak danych do podsumowania.</div>;
    }

    const sum = (pick: (s: SchoolYearSummary) => number) => summaries.reduce((total, s) => total + pick(s), 0);

    // The account balance is a running figure, so the overall value is the most recent year's.
    const rows: { label: string; values: number[]; total: number; colored?: boolean; emphasis?: boolean }[] = [
        { label: 'Przychód', values: summaries.map(s => s.totalIncome), total: sum(s => s.totalIncome) },
        { label: 'Wydatek', values: summaries.map(s => s.totalSpending), total: sum(s => s.totalSpending) },
        { label: 'Zwroty', values: summaries.map(s => s.totalRefunds), total: sum(s => s.totalRefunds) },
        { label: 'Bilans', values: summaries.map(s => s.balance), total: sum(s => s.balance), colored: true, emphasis: true },
        { label: 'Saldo', values: summaries.map(s => s.accountBalance), total: summaries[summaries.length - 1].accountBalance, colored: true },
    ];

    return (
        <div className="surface table-card">
            <div className="table-scroll">
                <table className="table data-table summary-table">
                    <thead>
                        <tr>
                            <th scope="col"><span className="visually-hidden">Pozycja</span></th>
                            {summaries.map(summary => (
                                <th key={summary.schoolYear} className="num" scope="col">{summary.schoolYear}</th>
                            ))}
                            <th className="num total-col" scope="col">Całkowity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.label} className={row.emphasis ? 'emphasis' : undefined}>
                                <th className="row-label" scope="row">{row.label}</th>
                                {row.values.map((value, index) => (
                                    <td key={summaries[index].schoolYear} className={`num${row.colored ? ` tone-${amountTone(value)}` : ''}`}>
                                        {formatMoney(value, { fixed: true })}
                                    </td>
                                ))}
                                <td className={`num total-col${row.colored ? ` tone-${amountTone(row.total)}` : ''}`}>
                                    {formatMoney(row.total, { fixed: true })}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SchoolYearSummaryDisplay;
